import React, { useEffect, useState } from "react";
import { db, auth } from "../firebase";
import { collection, query, getDocs, doc, getDoc, setDoc, deleteDoc, serverTimestamp } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import Layout from "../components/Layout";
import { useNavigate } from "react-router-dom";
import { FaPlus, FaTrash, FaEdit, FaSave, FaTimes } from "react-icons/fa";

const SupplierManager = () => {
    const navigate = useNavigate();
    const [userProfile, setUserProfile] = useState(null);
    const [suppliers, setSuppliers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(null); // ID of supplier being edited

    const initialForm = {
        id: "",
        name: "",
        gst: "",
        addr1: "",
        addr2: "",
        addr3: "",
        addr4: "",
        state: "",
        stateCode: "",
        pan: "",
        remarks: ""
    };
    const [form, setForm] = useState(initialForm);

    useEffect(() => {
        const unsub = onAuthStateChanged(auth, async (user) => {
            if (!user) { navigate("/"); return; }
            const snap = await getDoc(doc(db, "users", user.uid));
            if (snap.exists()) {
                const profile = snap.data();
                if (!["superadmin", "admin"].includes(profile.role)) {
                    alert("Unauthorized");
                    navigate("/dashboard");
                    return;
                }
                setUserProfile(profile);
                loadSuppliers();
            }
        });
        return () => unsub();
    }, [navigate]);

    const loadSuppliers = async () => {
        setLoading(true);
        try {
            const q = query(collection(db, "suppliers"));
            const snap = await getDocs(q);
            const list = [];
            snap.forEach(d => list.push({ id: d.id, ...d.data() }));
            setSuppliers(list);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!form.name || !form.gst) { alert("Name and GST are required"); return; }
        try {
            const sid = form.id || form.gst.toUpperCase().substring(0, 15);
            await setDoc(doc(db, "suppliers", sid), {
                ...form,
                id: sid,
                updatedAt: serverTimestamp()
            });
            alert("Supplier Saved");
            setForm(initialForm);
            setIsEditing(null);
            loadSuppliers();
        } catch (err) {
            alert(err.message);
        }
    };

    const handleEdit = (s) => {
        setForm(s);
        setIsEditing(s.id);
        window.scrollTo(0, 0);
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this supplier?")) return;
        await deleteDoc(doc(db, "suppliers", id));
        loadSuppliers();
    };

    return (
        <Layout>
            <div className="layout-content">
                <div style={{ marginBottom: 40 }}>
                    <h1 style={{ fontSize: 32, fontWeight: 900 }}>SUPPLIER ONBOARDING</h1>
                    <p style={{ color: "var(--text-muted)" }}>Manage primary manufacturers and raw material providers.</p>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 380px", gap: 32 }}>
                    
                    {/* LIST */}
                    <div className="premium-card" style={{ padding: 0 }}>
                        <table className="enterprise-table">
                            <thead>
                                <tr>
                                    <th>Supplier Details</th>
                                    <th>GST / PAN</th>
                                    <th>Location</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {suppliers.map(s => (
                                    <tr key={s.id}>
                                        <td>
                                            <div style={{ fontWeight: 800 }}>{s.name}</div>
                                            <div style={{ fontSize: 11, opacity: 0.6 }}>{s.remarks}</div>
                                        </td>
                                        <td>
                                            <div style={{ fontSize: 12, fontWeight: 700 }}>{s.gst}</div>
                                            <div style={{ fontSize: 10 }}>{s.pan}</div>
                                        </td>
                                        <td>
                                            <div style={{ fontSize: 11 }}>{s.state} ({s.stateCode})</div>
                                        </td>
                                        <td>
                                            <div style={{ display: "flex", gap: 10 }}>
                                                <button onClick={() => handleEdit(s)} className="btn-secondary" style={{ padding: 8 }}><FaEdit /></button>
                                                <button onClick={() => handleDelete(s.id)} className="btn-secondary" style={{ padding: 8, color: "var(--error)" }}><FaTrash /></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* FORM */}
                    <div style={{ position: "sticky", top: 100 }}>
                        <div className="premium-card">
                            <h3 style={{ marginBottom: 20, fontSize: 14, fontWeight: 800 }}>{isEditing ? "EDIT SUPPLIER" : "NEW SUPPLIER"}</h3>
                            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                                <div><label style={labelS}>Supplier Name</label><input className="form-input" value={form.name} onChange={e=>setForm({...form, name: e.target.value})} /></div>
                                <div><label style={labelS}>GSTIN</label><input className="form-input" value={form.gst} onChange={e=>setForm({...form, gst: e.target.value.toUpperCase()})} /></div>
                                
                                <div>
                                    <label style={labelS}>Address</label>
                                    <input className="form-input" style={{marginBottom:4}} placeholder="Line 1" value={form.addr1} onChange={e=>setForm({...form, addr1: e.target.value})} />
                                    <input className="form-input" style={{marginBottom:4}} placeholder="Line 2" value={form.addr2} onChange={e=>setForm({...form, addr2: e.target.value})} />
                                    <input className="form-input" style={{marginBottom:4}} placeholder="Line 3" value={form.addr3} onChange={e=>setForm({...form, addr3: e.target.value})} />
                                    <input className="form-input" placeholder="Line 4" value={form.addr4} onChange={e=>setForm({...form, addr4: e.target.value})} />
                                </div>

                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                                    <div><label style={labelS}>State</label><input className="form-input" value={form.state} onChange={e=>setForm({...form, state: e.target.value})} /></div>
                                    <div><label style={labelS}>State Code</label><input className="form-input" value={form.stateCode} onChange={e=>setForm({...form, stateCode: e.target.value})} /></div>
                                </div>

                                <div><label style={labelS}>PAN</label><input className="form-input" value={form.pan} onChange={e=>setForm({...form, pan: e.target.value.toUpperCase()})} /></div>
                                <div><label style={labelS}>Remarks</label><textarea className="form-input" value={form.remarks} onChange={e=>setForm({...form, remarks: e.target.value})} /></div>

                                <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
                                    <button onClick={handleSave} className="btn-primary" style={{ flex: 1 }}><FaSave /> SAVE</button>
                                    {isEditing && <button onClick={() => { setIsEditing(null); setForm(initialForm); }} className="btn-secondary"><FaTimes /></button>}
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </Layout>
    );
};

const labelS = { fontSize: 10, fontWeight: 800, color: "var(--text-muted)", marginBottom: 4, display: "block" };

export default SupplierManager;
